import React, { useEffect, useState, useRef, useCallback } from 'react';
import '../../Css/Botones/botones.css';
import '../../Css/catalogobase/catalogobase.css';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Badge } from 'primereact/badge';
import Navbar from '../../components/navbar/navbar';
import Sidebar from '../../components/sidebar/sidebar';
import { API_URLS } from '../../Services/servicioURL';
import { BASE_URL } from '../../Services/servicioURL';

// Interfaces actualizadas para coincidir con el backend
interface Producto {
    id: number;
    producto: string;
    descripcion: string;
    cantidad: number;
}

interface ProductoCatalogo {
    cve: string;
    descripcion: string;
    modelo: string;
    serie: string;
}

interface Proveedor {
    id: number;
    nombre: string;
}

interface Usuario {
    id: number;
    nombre: string;
    // Agregar más campos según tu entidad Usuario
}

interface ProductoBackend {
    cve: string;
    descripcion: string;
    modelo: string;
    serie: string;
    // Agregar más campos según tu entidad Producto
}

interface EntradaDetalleBackend {
    folio: string;
    id: number;
    cve_producto: string;
    cantidad: number;
    producto: ProductoBackend; // Relación con producto
}

// Interface actualizada para coincidir con lo que devuelve el backend
interface EntradaBackend {
    folio: string;
    fecha: string;
    referencia: string;
    observaciones: string;
    id_proveedor: number;
    id_usuario_capturo: number;
    proveedor: Proveedor; // Objeto completo del proveedor
    usuarioCapturo: Usuario; // Objeto completo del usuario
    entradaDetalle: EntradaDetalleBackend[]; // Array de detalles con productos
}

// Interface para uso interno del componente (simplificada)
interface EntradaDetalle {
    folio: string;
    id: number;
    cve_producto: string;
    cantidad: number;
}

interface EntradaType {
    folio: string;
    fecha: string;
    proveedor: string; // Solo el nombre para mostrar
    referencia: string;
    observaciones: string;
    id_proveedor: number;
    entradaDetalle: EntradaDetalle[];
}

// Funciones de API consolidadas
const apiService = {
    obtenerProveedores: async (): Promise<Proveedor[]> => {
        const response = await fetch(`${BASE_URL}/proveedor/getProveedores`);
        if (!response.ok) throw new Error('Error al obtener los Proveedores');
        return response.json();
    },

    obtenerProductos: async (): Promise<ProductoCatalogo[]> => {
        const response = await fetch(API_URLS.productos.obtener);
        if (!response.ok) throw new Error('Error al obtener los productos');
        return response.json();
    },

    obtenerEntradas: async (): Promise<EntradaType[]> => {
        const response = await fetch(API_URLS.entradas.obtener);
        if (!response.ok) throw new Error('Error al obtener las entradas');
        const entradasBackend: EntradaBackend[] = await response.json();
        
        // Transformar los datos del backend al formato que usa el frontend
        return entradasBackend.map(entrada => ({
            folio: entrada.folio,
            fecha: entrada.fecha,
            proveedor: entrada.proveedor?.nombre || 'Sin proveedor',
            referencia: entrada.referencia,
            observaciones: entrada.observaciones,
            id_proveedor: entrada.id_proveedor,
            entradaDetalle: entrada.entradaDetalle?.map(detalle => ({
                folio: detalle.folio,
                id: detalle.id,
                cve_producto: detalle.cve_producto,
                cantidad: detalle.cantidad
            })) || []
        }));
    },

    obtenerUltimoFolio: async (): Promise<number> => {
        const response = await fetch(`${BASE_URL}/entrada/getEntradas`);
        if (!response.ok) throw new Error('No se pudo obtener el último folio');
        const data: EntradaBackend[] = await response.json();
        const folios = data.map((e) => parseInt(e.folio)).filter(Number);
        return folios.length ? Math.max(...folios) : 0;
    }
};

const Entradas: React.FC = () => {
    // Estados principales consolidados
    const [formData, setFormData] = useState({
        folio: '',
        fecha: new Date().toISOString().split('T')[0],
        proveedor: null as Proveedor | null,
        referencia: '',
        observaciones: ''
    });

    const [productData, setProductData] = useState({
        nombreProducto: '',
        descripcionProducto: '',
        cantidadProducto: ''
    });

    const [catalogos, setCatalogos] = useState({
        proveedores: [] as Proveedor[],
        productos: [] as ProductoCatalogo[],
        entradas: [] as EntradaType[]
    });

    const [filteredData, setFilteredData] = useState({
        productos: [] as ProductoCatalogo[],
        entradas: [] as EntradaType[]
    });

    const [searchTerms, setSearchTerms] = useState({
        producto: '',
        entrada: ''
    });

    const [modals, setModals] = useState({
        productos: false,
        consultaEntradas: false
    });

    const [appState, setAppState] = useState({
        loading: true,
        mode: 'view' as 'view' | 'new' | 'edit',
        editar: false,
        selectedProductIndex: null as number | null,
        isFechaDisabled: true
    });

    const [products, setProducts] = useState<Producto[]>([]);
    const [nextId, setNextId] = useState<number>(1);
    const toast = useRef<Toast>(null);

    // Función para mostrar mensajes
    const showMessage = useCallback((severity: 'success' | 'info' | 'warn' | 'error', summary: string, detail: string) => {
        toast.current?.show({ severity, summary, detail, life: 3000 });
    }, []);

    // Cargar datos iniciales
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                setAppState(prev => ({ ...prev, loading: true }));
                const [proveedores, productos] = await Promise.all([
                    apiService.obtenerProveedores(),
                    apiService.obtenerProductos()
                ]);
                setCatalogos(prev => ({ ...prev, proveedores, productos }));
                setFilteredData(prev => ({ ...prev, productos }));
            } catch (error) {
                console.error('Error al cargar datos:', error);
                showMessage('error', 'Error', 'No se pudieron cargar los datos iniciales');
            } finally {
                setAppState(prev => ({ ...prev, loading: false }));
            }
        };
        loadInitialData();
    }, [showMessage]);

    // Obtener folio automático para nuevas entradas
    useEffect(() => {
        const cargarFolioAutomatico = async () => {
            if (appState.mode === 'new') {
                try {
                    const ultimoFolio = await apiService.obtenerUltimoFolio();
                    setFormData(prev => ({ ...prev, folio: (ultimoFolio + 1).toString() }));
                } catch (error) {
                    console.error('Error al cargar folio:', error);
                    setFormData(prev => ({ ...prev, folio: '1' }));
                }
            }
        };
        cargarFolioAutomatico();
    }, [appState.mode]);

    // Filtros consolidados
    useEffect(() => {
        const applyFilters = () => {
            // Filtrar productos
            if (searchTerms.producto) {
                const filtered = catalogos.productos.filter(p =>
                    Object.values(p).some(val =>
                        val?.toString().toLowerCase().includes(searchTerms.producto.toLowerCase())
                    )
                );
                setFilteredData(prev => ({ ...prev, productos: filtered }));
            } else {
                setFilteredData(prev => ({ ...prev, productos: catalogos.productos }));
            }

            // Filtrar entradas
            if (searchTerms.entrada) {
                const filtered = catalogos.entradas.filter(e =>
                    e.folio.toLowerCase().includes(searchTerms.entrada.toLowerCase()) ||
                    (e.observaciones || '').toLowerCase().includes(searchTerms.entrada.toLowerCase()) ||
                    (e.proveedor || '').toLowerCase().includes(searchTerms.entrada.toLowerCase()) ||
                    (e.referencia || '').toLowerCase().includes(searchTerms.entrada.toLowerCase())
                );
                setFilteredData(prev => ({ ...prev, entradas: filtered }));
            } else {
                setFilteredData(prev => ({ ...prev, entradas: catalogos.entradas }));
            }
        };
        applyFilters();
    }, [searchTerms, catalogos]);

    // Funciones de manejo de modales
    const openModal = useCallback(async (modalType: keyof typeof modals) => {
        try {
            setAppState(prev => ({ ...prev, loading: true }));
            switch (modalType) {
                case 'productos':
                    if (catalogos.productos.length === 0) {
                        const productos = await apiService.obtenerProductos();
                        setCatalogos(prev => ({ ...prev, productos }));
                        setFilteredData(prev => ({ ...prev, productos }));
                    }
                    break;
                case 'consultaEntradas':
                    const entradas = await apiService.obtenerEntradas();
                    setCatalogos(prev => ({ ...prev, entradas }));
                    setFilteredData(prev => ({ ...prev, entradas }));
                    break;
            }
            setModals(prev => ({ ...prev, [modalType]: true }));
            setSearchTerms(prev => ({ ...prev, [modalType === 'consultaEntradas' ? 'entrada' : modalType.slice(0, -1)]: '' }));
        } catch (error) {
            console.error('Error al abrir modal:', error);
            showMessage('error', 'Error', `No se pudieron cargar los ${modalType}`);
        } finally {
            setAppState(prev => ({ ...prev, loading: false }));
        }
    }, [catalogos, showMessage]);

    const closeModal = useCallback((modalType: keyof typeof modals) => {
        setModals(prev => ({ ...prev, [modalType]: false }));
    }, []);

    // Funciones de selección
    const seleccionarProducto = useCallback((producto: ProductoCatalogo) => {
        setProductData(prev => ({
            ...prev,
            nombreProducto: producto.cve,
            descripcionProducto: producto.descripcion,
            cantidadProducto: '1'
        }));
        closeModal('productos');
        showMessage('info', 'Producto seleccionado', `Se ha seleccionado: ${producto.descripcion}`);
    }, [closeModal, showMessage]);

    const seleccionarEntrada = useCallback((entrada: EntradaType) => {
        setFormData({
            folio: entrada.folio,
            fecha: entrada.fecha,
            proveedor: { id: entrada.id_proveedor, nombre: entrada.proveedor },
            referencia: entrada.referencia,
            observaciones: entrada.observaciones
        });

        if (Array.isArray(entrada.entradaDetalle)) {
            const productosConDescripcion = entrada.entradaDetalle.map(d => {
                const productoCatalogo = catalogos.productos.find(p => p.cve === d.cve_producto);
                return {
                    id: d.id,
                    producto: d.cve_producto,
                    descripcion: productoCatalogo?.descripcion || 'Descripción no encontrada',
                    cantidad: d.cantidad
                };
            });
            setProducts(productosConDescripcion);
        } else {
            setProducts([]);
        }

        setAppState(prev => ({ ...prev, mode: 'view' }));
        closeModal('consultaEntradas');
    }, [catalogos, closeModal]);

    // Funciones de productos
    const limpiarCamposProducto = useCallback(() => {
        setProductData({
            nombreProducto: '',
            descripcionProducto: '',
            cantidadProducto: ''
        });
        setAppState(prev => ({ ...prev, editar: false, selectedProductIndex: null }));
    }, []);

    const agregarProducto = useCallback(() => {
        const { nombreProducto, descripcionProducto, cantidadProducto } = productData;
        if (!nombreProducto || !descripcionProducto || !cantidadProducto) {
            showMessage('warn', 'Campos requeridos', 'Todos los campos del producto son obligatorios');
            return;
        }

        const newProduct: Producto = {
            id: nextId,
            producto: nombreProducto,
            descripcion: descripcionProducto,
            cantidad: parseInt(cantidadProducto)
        };

        setProducts(prev => [...prev, newProduct]);
        setNextId(prev => prev + 1);
        limpiarCamposProducto();
        showMessage('success', '¡Producto agregado!', 'El producto se agregó correctamente a la lista');
    }, [productData, nextId, limpiarCamposProducto, showMessage]);

    const actualizarProducto = useCallback(() => {
        const { nombreProducto, descripcionProducto, cantidadProducto } = productData;
        if (!nombreProducto || !descripcionProducto || !cantidadProducto || appState.selectedProductIndex === null) {
            showMessage('warn', 'Campos requeridos', 'Todos los campos del producto son obligatorios');
            return;
        }

        const updatedProduct: Producto = {
            id: products[appState.selectedProductIndex].id,
            producto: nombreProducto,
            descripcion: descripcionProducto,
            cantidad: parseInt(cantidadProducto)
        };

        setProducts(prev => {
            const updated = [...prev];
            updated[appState.selectedProductIndex!] = updatedProduct;
            return updated;
        });

        limpiarCamposProducto();
        showMessage('success', '¡Producto actualizado!', 'Los cambios se guardaron correctamente');
    }, [productData, products, appState.selectedProductIndex, limpiarCamposProducto, showMessage]);

    const eliminarProducto = useCallback((index: number) => {
        const confirmacion = window.confirm('⚠️ ¿Está seguro de eliminar este producto?\n\nEsta acción no se puede deshacer.');
        if (confirmacion) {
            setProducts(prev => prev.filter((_, i) => i !== index));
            showMessage('success', '¡Producto eliminado!', 'El producto se eliminó de la lista');
        }
    }, [showMessage]);

    const seleccionarProductoParaEditar = useCallback((product: Producto, index: number) => {
        setProductData({
            nombreProducto: product.producto,
            descripcionProducto: product.descripcion,
            cantidadProducto: product.cantidad.toString()
        });
        setAppState(prev => ({ ...prev, editar: true, selectedProductIndex: index }));
        showMessage('info', 'Producto seleccionado', 'Puede editar los campos y actualizar el producto');
    }, [showMessage]);

    // Funciones principales
    const limpiarFormulario = useCallback(() => {
        setFormData({
            folio: '',
            fecha: new Date().toISOString().split('T')[0],
            proveedor: null,
            referencia: '',
            observaciones: ''
        });
        setProducts([]);
        limpiarCamposProducto();
    }, [limpiarCamposProducto]);

    const handleNuevo = useCallback(() => {
        limpiarFormulario();
        setAppState(prev => ({ ...prev, mode: 'new', isFechaDisabled: false }));
    }, [limpiarFormulario]);

    const handleEditar = useCallback(() => {
        if (!formData.folio) {
            showMessage('warn', 'Advertencia', 'Seleccione una entrada para editar');
            return;
        }
        setAppState(prev => ({ ...prev, mode: 'edit', isFechaDisabled: false }));
    }, [formData.folio, showMessage]);

    const handleEliminar = useCallback(async () => {
        if (!formData.folio) {
            showMessage('warn', 'Advertencia', 'Seleccione una entrada para eliminar');
            return;
        }

        const confirmacion = window.confirm(`⚠️ ¿Está seguro de eliminar la entrada con folio: ${formData.folio}?\n\nEsta acción no se puede deshacer.`);
        if (confirmacion) {
            try {
                const response = await fetch(`${BASE_URL}/entrada/eliminarEntrada/${formData.folio}`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' }
                });
                if (!response.ok) throw new Error('Error al eliminar la entrada');
                showMessage('success', '¡Entrada eliminada!', 'La entrada se eliminó correctamente');
                limpiarFormulario();
            } catch (error) {
                console.error('Error al eliminar:', error);
                showMessage('error', 'Error', 'No se pudo eliminar la entrada');
            }
        }
    }, [formData.folio, limpiarFormulario, showMessage]);

    const handleCancelar = useCallback(() => {
        setAppState(prev => ({ ...prev, mode: 'view', isFechaDisabled: true }));
        limpiarFormulario();
    }, [limpiarFormulario]);

    const handleGuardar = useCallback(async () => {
        const { folio, fecha, proveedor, referencia, observaciones } = formData;

        if (!folio || !fecha || !proveedor || !referencia) {
            showMessage('warn', 'Campos requeridos', 'Todos los campos marcados con * son obligatorios');
            return;
        }

        if (products.length === 0) {
            showMessage('warn', 'Sin productos', 'Debe agregar al menos un producto a la entrada');
            return;
        }

        const idUsuarioString = localStorage.getItem('idUsuario');
        const idUsuario = idUsuarioString ? parseInt(idUsuarioString) : null;

        if (!idUsuario) {
            showMessage('warn', 'Error de sesión', 'No se pudo identificar al usuario. Vuelva a iniciar sesión.');
            return;
        }

        const entradaDetalle = products.map((producto) => ({
            folio,
            ...(producto.id && { id: producto.id }),
            cve_producto: producto.producto,
            cantidad: producto.cantidad
        }));

        const payload = {
            folio,
            fecha,
            id_usuario_capturo: idUsuario,
            id_proveedor: proveedor.id,
            referencia,
            observaciones: observaciones || '',
            entradaDetalle
        };

        try {
            const url = appState.mode === 'new' 
                ? `${BASE_URL}/entrada/registrarEntrada`
                : `${BASE_URL}/entrada/actualizarEntrada/${folio}`;
            
            const method = appState.mode === 'new' ? 'POST' : 'PUT';

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const text = await response.text();
                throw new Error(`Error del servidor: ${text}`);
            }

            showMessage('success', 
                appState.mode === 'new' ? '¡Entrada registrada!' : '¡Entrada actualizada!',
                appState.mode === 'new' ? 'La entrada se registró exitosamente' : 'Los cambios se guardaron correctamente'
            );

            limpiarFormulario();
            setAppState(prev => ({ ...prev, mode: 'view', isFechaDisabled: true }));
        } catch (error: any) {
            console.error('Error al guardar:', error);
            showMessage('error', 'Error al guardar', error.message || 'No se pudo guardar la entrada');
        }
    }, [formData, products, appState.mode, limpiarFormulario, showMessage]);

    // Función para obtener información del modo actual
    const getModeIndicator = () => {
        switch (appState.mode) {
            case 'new':
                return { text: 'NUEVA ENTRADA', color: '#10b981', icon: 'pi-plus' };
            case 'edit':
                return { text: 'EDITANDO ENTRADA', color: '#f59e0b', icon: 'pi-pencil' };
            default:
                return { text: 'MODO CONSULTA', color: '#6b7280', icon: 'pi-eye' };
        }
    };

    const modeInfo = getModeIndicator();

    // Componentes de renderizado para tabla de productos
    const actionBodyTemplate = (rowData: Producto, options: any) => (
        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
            <Button 
                icon="pi pi-pencil" 
                size="small"
                severity="warning"
                rounded
                onClick={() => seleccionarProductoParaEditar(rowData, options.rowIndex)}
                disabled={appState.mode === 'view'}
                tooltip="Editar producto"
                style={{ width: '28px', height: '28px' }}
            />
            <Button 
                icon="pi pi-trash" 
                size="small"
                severity="danger"
                rounded
                onClick={() => eliminarProducto(options.rowIndex)}
                disabled={appState.mode === 'view'}
                tooltip="Eliminar producto"
                style={{ width: '28px', height: '28px' }}
            />
        </div>
    );

    const cantidadBodyTemplate = (rowData: Producto) => (
        <div style={{ textAlign: 'center' }}>
            <Badge value={rowData.cantidad} severity="info" />
        </div>
    );

    return (
        <>
            <Navbar />
            <Sidebar />
            <Toast ref={toast} />

            {/* MODAL - Catálogo de Productos */}
            {modals.productos && (
                <div className="overlay" onClick={() => closeModal('productos')} style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div className="floating-window" onClick={(e) => e.stopPropagation()} style={{
                        backgroundColor: 'white', borderRadius: '12px', padding: '24px',
                        maxWidth: '1000px', width: '95%', height: '80vh',
                        display: 'flex', flexDirection: 'column',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.4rem', fontWeight: '600' }}>
                                📦 Catálogo de Productos
                            </h3>
                            <Button
                                icon="pi pi-times"
                                className="p-button-text p-button-rounded"
                                onClick={() => closeModal('productos')}
                                style={{ color: '#6b7280' }}
                            />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ position: 'relative' }}>
                                <i className="pi pi-search" style={{
                                    position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
                                    color: '#9ca3af', fontSize: '0.9rem'
                                }}></i>
                                <InputText
                                    style={{
                                        width: '100%', height: '36px', paddingLeft: '35px',
                                        border: '2px solid #e5e7eb', borderRadius: '6px', fontSize: '0.9rem'
                                    }}
                                    placeholder="Buscar por clave, descripción o modelo..."
                                    value={searchTerms.producto}
                                    onChange={(e) => setSearchTerms(prev => ({ ...prev, producto: e.target.value }))}
                                />
                            </div>
                        </div>

                        <div style={{ 
                            marginBottom: '12px', fontSize: '0.85em', color: '#6b7280',
                            padding: '6px 10px', backgroundColor: '#f9fafb', borderRadius: '4px',
                            border: '1px solid #e5e7eb'
                        }}>
                            📊 Mostrando <strong>{filteredData.productos.length}</strong> de <strong>{catalogos.productos.length}</strong> productos
                        </div>

                        {/* Contenedor de tabla con altura fija y scroll */}
                        <div style={{ 
                            flex: 1, 
                            minHeight: 0, // Importante para que funcione el flex
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            overflow: 'hidden'
                        }}>
                            <DataTable 
                                value={filteredData.productos}
                                scrollable 
                                scrollHeight="100%"
                                loading={appState.loading}
                                emptyMessage="📭 No se encontraron productos"
                                size="small"
                                stripedRows
                                rowHover
                                onRowClick={(e) => seleccionarProducto(e.data as ProductoCatalogo)}
                                style={{ 
                                    fontSize: '0.85rem', 
                                    cursor: 'pointer',
                                    height: '100%'
                                }}
                                rowClassName={() => 'cursor-pointer'}
                            >
                                <Column 
                                    field="cve" 
                                    header="🔑 Clave" 
                                    style={{ width: '120px', padding: '6px 8px', fontSize: '0.85rem' }}
                                    headerStyle={{ padding: '8px', fontSize: '0.9rem', fontWeight: '600' }}
                                />
                                <Column 
                                    field="descripcion" 
                                    header="📝 Descripción" 
                                    style={{ minWidth: '250px', padding: '6px 8px', fontSize: '0.85rem' }}
                                    headerStyle={{ padding: '8px', fontSize: '0.9rem', fontWeight: '600' }}
                                />
                                <Column 
                                    field="modelo" 
                                    header="🏷️ Modelo" 
                                    style={{ width: '150px', padding: '6px 8px', fontSize: '0.85rem' }}
                                    headerStyle={{ padding: '8px', fontSize: '0.9rem', fontWeight: '600' }}
                                />
                                <Column 
                                    field="serie" 
                                    header="🔢 Serie" 
                                    style={{ width: '120px', padding: '6px 8px', fontSize: '0.85rem' }}
                                    headerStyle={{ padding: '8px', fontSize: '0.9rem', fontWeight: '600' }}
                                />
                            </DataTable>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL - Tabla de Consulta de Entradas */}
            {modals.consultaEntradas && (
                <div className="overlay" onClick={() => closeModal('consultaEntradas')} style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div className="floating-window" onClick={(e) => e.stopPropagation()} style={{
                        backgroundColor: 'white', borderRadius: '12px', padding: '20px',
                        maxWidth: '900px', width: '95%', height: '80vh',
                        display: 'flex', flexDirection: 'column',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.4rem', fontWeight: '600' }}>
                                📋 Consultar Entradas
                            </h3>
                            <Button
                                icon="pi pi-times"
                                className="p-button-text p-button-rounded"
                                onClick={() => closeModal('consultaEntradas')}
                                style={{ color: '#6b7280' }}
                            />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ position: 'relative' }}>
                                <i className="pi pi-search" style={{
                                    position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
                                    color: '#9ca3af', fontSize: '0.9rem'
                                }}></i>
                                <InputText
                                    style={{
                                        width: '100%', height: '36px', paddingLeft: '35px',
                                        border: '2px solid #e5e7eb', borderRadius: '6px', fontSize: '0.9rem'
                                    }}
                                    placeholder="Buscar por folio, proveedor, referencia u observaciones..."
                                    value={searchTerms.entrada}
                                    onChange={(e) => setSearchTerms(prev => ({ ...prev, entrada: e.target.value }))}
                                />
                            </div>
                        </div>

                        <div style={{ 
                            marginBottom: '12px', fontSize: '0.85em', color: '#6b7280',
                            padding: '6px 10px', backgroundColor: '#f9fafb', borderRadius: '4px',
                            border: '1px solid #e5e7eb'
                        }}>
                            📊 Mostrando <strong>{filteredData.entradas.length}</strong> de <strong>{catalogos.entradas.length}</strong> entradas
                        </div>

                        {/* Contenedor de tabla con altura fija y scroll */}
                        <div style={{ 
                            flex: 1, 
                            minHeight: 0, // Importante para que funcione el flex
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            overflow: 'hidden'
                        }}>
                            <DataTable 
                                value={filteredData.entradas}
                                scrollable 
                                scrollHeight="100%"
                                emptyMessage="📭 No hay resultados"
                                size="small"
                                stripedRows
                                rowHover
                                onRowClick={(e) => seleccionarEntrada(e.data as EntradaType)}
                                style={{ 
                                    fontSize: '0.85rem', 
                                    cursor: 'pointer',
                                    height: '100%'
                                }}
                                rowClassName={() => 'cursor-pointer'}
                            >
                                <Column 
                                    field="folio" 
                                    header="📄 Folio" 
                                    style={{ width: '100px', padding: '6px 8px', fontSize: '0.85rem', textAlign: 'center' }}
                                    headerStyle={{ padding: '8px', fontSize: '0.9rem', fontWeight: '600' }}
                                />
                                <Column 
                                    field="fecha" 
                                    header="📅 Fecha" 
                                    style={{ width: '120px', padding: '6px 8px', fontSize: '0.85rem', textAlign: 'center' }}
                                    headerStyle={{ padding: '8px', fontSize: '0.9rem', fontWeight: '600' }}
                                />
                                <Column 
                                    field="proveedor" 
                                    header="🏢 Proveedor" 
                                    style={{ width: '180px', padding: '6px 8px', fontSize: '0.85rem' }}
                                    headerStyle={{ padding: '8px', fontSize: '0.9rem', fontWeight: '600' }}
                                />
                                <Column 
                                    field="referencia" 
                                    header="🔗 Referencia" 
                                    style={{ width: '140px', padding: '6px 8px', fontSize: '0.85rem' }}
                                    headerStyle={{ padding: '8px', fontSize: '0.9rem', fontWeight: '600' }}
                                />
                                <Column 
                                    field="observaciones" 
                                    header="📝 Observaciones" 
                                    style={{ minWidth: '200px', padding: '6px 8px', fontSize: '0.85rem' }}
                                    headerStyle={{ padding: '8px', fontSize: '0.9rem', fontWeight: '600' }}
                                />
                            </DataTable>
                        </div>
                    </div>
                </div>
            )}

            {/* Contenido principal */}
            <div className="content-wrapper" style={{
                backgroundImage: 'url(/dist/img/FondoLogin.png)',
                backgroundSize: 'cover', backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed',
                minHeight: '100vh', position: 'relative', padding: '20px'
            }}>
                
                {/* Header principal */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px', padding: '24px', marginBottom: '20px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h1 style={{ 
                            margin: 0, color: '#1f2937', fontSize: '2rem', fontWeight: '700',
                            display: 'flex', alignItems: 'center', gap: '12px'
                        }}>
                            📦 Gestión de Entradas
                        </h1>
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            padding: '8px 16px', backgroundColor: modeInfo.color,
                            color: 'white', borderRadius: '20px',
                            fontSize: '0.85rem', fontWeight: '600'
                        }}>
                            <i className={`pi ${modeInfo.icon}`}></i>
                            {modeInfo.text}
                        </div>
                    </div>
                    <div style={{ height: '2px', background: 'linear-gradient(90deg, #3b82f6, #10b981)', borderRadius: '1px' }}></div>
                </div>

                {/* Formulario principal */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px', padding: '24px', marginBottom: '20px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    
                    {/* Botones de acción */}
                    <div style={{ marginBottom: '24px' }}>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            {appState.mode === 'view' ? (
                                <>
                                    <Button
                                        icon="pi pi-search"
                                        label="Consultar Entradas"
                                        className="p-button-info p-button-raised"
                                        onClick={() => openModal('consultaEntradas')}
                                        style={{ 
                                            height: '44px', fontSize: '0.95rem', fontWeight: '600',
                                            borderRadius: '8px', minWidth: '160px'
                                        }}
                                    />
                                    <Button
                                        icon="pi pi-plus"
                                        label="Nueva Entrada"
                                        className="p-button-success p-button-raised"
                                        onClick={handleNuevo}
                                        style={{ 
                                            height: '44px', fontSize: '0.95rem', fontWeight: '600',
                                            borderRadius: '8px', minWidth: '140px'
                                        }}
                                    />
                                    <Button
                                        icon="pi pi-pencil"
                                        label="Editar"
                                        className="p-button-warning p-button-raised"
                                        onClick={handleEditar}
                                        disabled={!formData.folio}
                                        style={{ 
                                            height: '44px', fontSize: '0.95rem', fontWeight: '600',
                                            borderRadius: '8px', minWidth: '120px',
                                            opacity: !formData.folio ? 0.5 : 1
                                        }}
                                    />
                                    <Button
                                        icon="pi pi-trash"
                                        label="Eliminar"
                                        className="p-button-danger p-button-raised"
                                        onClick={handleEliminar}
                                        disabled={!formData.folio}
                                        style={{ 
                                            height: '44px', fontSize: '0.95rem', fontWeight: '600',
                                            borderRadius: '8px', minWidth: '120px',
                                            opacity: !formData.folio ? 0.5 : 1
                                        }}
                                    />
                                </>
                            ) : (
                                <>
                                    <Button
                                        icon="pi pi-save"
                                        label="Guardar"
                                        className="p-button-success p-button-raised"
                                        onClick={handleGuardar}
                                        style={{ 
                                            height: '44px', fontSize: '0.95rem', fontWeight: '600',
                                            borderRadius: '8px', minWidth: '120px'
                                        }}
                                    />
                                    <Button
                                        icon="pi pi-times"
                                        label="Cancelar"
                                        className="p-button-secondary p-button-raised"
                                        onClick={handleCancelar}
                                        style={{ 
                                            height: '44px', fontSize: '0.95rem', fontWeight: '600',
                                            borderRadius: '8px', minWidth: '120px'
                                        }}
                                    />
                                </>
                            )}
                        </div>
                    </div>

                    {/* Formulario de datos generales */}
                    <form onSubmit={(e) => e.preventDefault()} autoComplete="on">
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                            gap: '20px', 
                            marginBottom: '24px'
                        }}>
                            <div>
                                <label style={{ 
                                    display: 'block', fontSize: '0.95rem', fontWeight: '600',
                                    marginBottom: '8px', color: '#374151'
                                }}>
                                    📄 Folio <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <InputText 
                                    value={formData.folio} 
                                    onChange={(e) => setFormData(prev => ({ ...prev, folio: e.target.value }))} 
                                    disabled 
                                    style={{
                                        width: '100%', height: '40px', fontSize: '0.95rem',
                                        padding: '0 12px', border: '2px solid #e5e7eb',
                                        borderRadius: '8px', backgroundColor: '#f9fafb'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ 
                                    display: 'block', fontSize: '0.95rem', fontWeight: '600',
                                    marginBottom: '8px', color: '#374151'
                                }}>
                                    📅 Fecha <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <InputText 
                                    type="date" 
                                    value={formData.fecha}
                                    onChange={(e) => setFormData(prev => ({ ...prev, fecha: e.target.value }))}
                                    disabled={appState.isFechaDisabled}
                                    style={{
                                        width: '100%', height: '40px', fontSize: '0.95rem',
                                        padding: '0 12px',
                                        border: `2px solid ${appState.isFechaDisabled ? '#e5e7eb' : '#3b82f6'}`,
                                        borderRadius: '8px',
                                        backgroundColor: appState.isFechaDisabled ? '#f9fafb' : '#ffffff'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ 
                                    display: 'block', fontSize: '0.95rem', fontWeight: '600',
                                    marginBottom: '8px', color: '#374151'
                                }}>
                                    🏢 Proveedor <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                {appState.loading ? (
                                    <div style={{ 
                                        padding: '12px', color: '#6b7280', fontStyle: 'italic',
                                        border: '2px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f9fafb'
                                    }}>
                                        🔄 Cargando proveedores...
                                    </div>
                                ) : (
                                    <Dropdown 
                                        value={formData.proveedor} 
                                        options={catalogos.proveedores}
                                        optionLabel="nombre"
                                        onChange={(e) => setFormData(prev => ({ ...prev, proveedor: e.value }))}
                                        disabled={appState.mode === 'view'}
                                        placeholder="Seleccione un proveedor"
                                        style={{
                                            width: '100%', height: '40px',
                                            border: `2px solid ${appState.mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                            borderRadius: '8px'
                                        }}
                                    />
                                )}
                            </div>

                            <div>
                                <label style={{ 
                                    display: 'block', fontSize: '0.95rem', fontWeight: '600',
                                    marginBottom: '8px', color: '#374151'
                                }}>
                                    🔗 Referencia <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <InputText 
                                    value={formData.referencia} 
                                    onChange={(e) => setFormData(prev => ({ ...prev, referencia: e.target.value }))}
                                    disabled={appState.mode === 'view'}
                                    placeholder="Ingrese la referencia"
                                    style={{
                                        width: '100%', height: '40px', fontSize: '0.95rem',
                                        padding: '0 12px',
                                        border: `2px solid ${appState.mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                        borderRadius: '8px',
                                        backgroundColor: appState.mode === 'view' ? '#f9fafb' : '#ffffff'
                                    }}
                                />
                            </div>
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <label style={{ 
                                display: 'block', fontSize: '0.95rem', fontWeight: '600',
                                marginBottom: '8px', color: '#374151'
                            }}>
                                📝 Observaciones
                            </label>
                            <InputTextarea 
                                value={formData.observaciones} 
                                onChange={(e) => setFormData(prev => ({ ...prev, observaciones: e.target.value }))}
                                rows={3}
                                disabled={appState.mode === 'view'}
                                placeholder="Ingrese observaciones adicionales..."
                                style={{
                                    width: '100%', fontSize: '0.95rem', padding: '12px',
                                    border: `2px solid ${appState.mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                    borderRadius: '8px',
                                    backgroundColor: appState.mode === 'view' ? '#f9fafb' : '#ffffff',
                                    resize: 'vertical'
                                }}
                            />
                        </div>
                    </form>
                </div>

                {/* Sección de productos */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px', padding: '24px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <h3 style={{ 
                        margin: '0 0 20px 0', color: '#374151', fontSize: '1.3rem', fontWeight: '600',
                        display: 'flex', alignItems: 'center', gap: '8px'
                    }}>
                        📦 Productos de la Entrada
                        <Badge value={products.length} severity="info" style={{ marginLeft: '8px' }} />
                    </h3>

                    {/* Formulario de productos */}
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                        gap: '16px', 
                        marginBottom: '20px',
                        padding: '20px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                    }}>
                        <div>
                            <label style={{ 
                                display: 'block', fontSize: '0.9rem', fontWeight: '600',
                                marginBottom: '6px', color: '#374151'
                            }}>
                                🔑 Producto <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <InputText 
                                    value={productData.nombreProducto} 
                                    disabled 
                                    placeholder="Seleccione un producto"
                                    style={{
                                        flex: 1, height: '36px', fontSize: '0.9rem',
                                        border: '2px solid #e5e7eb', borderRadius: '6px',
                                        backgroundColor: '#f9fafb'
                                    }}
                                />
                                <Button 
                                    icon="pi pi-search" 
                                    onClick={() => openModal('productos')} 
                                    disabled={appState.mode === 'view'}
                                    size="small"
                                    style={{ height: '36px', borderRadius: '6px' }}
                                    tooltip="Buscar producto"
                                />
                            </div>
                        </div>

                        <div>
                            <label style={{ 
                                display: 'block', fontSize: '0.9rem', fontWeight: '600',
                                marginBottom: '6px', color: '#374151'
                            }}>
                                📝 Descripción <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <InputText 
                                value={productData.descripcionProducto} 
                                disabled 
                                style={{
                                    width: '100%', height: '36px', fontSize: '0.9rem',
                                    border: '2px solid #e5e7eb', borderRadius: '6px',
                                    backgroundColor: '#f9fafb'
                                }}
                            />
                        </div>

                        <div>
                            <label style={{ 
                                display: 'block', fontSize: '0.9rem', fontWeight: '600',
                                marginBottom: '6px', color: '#374151'
                            }}>
                                📊 Cantidad <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <InputText 
                                type="number" 
                                min="1" 
                                value={productData.cantidadProducto}
                                onChange={(e) => setProductData(prev => ({ ...prev, cantidadProducto: e.target.value }))}
                                disabled={appState.mode === 'view'}
                                placeholder="0"
                                style={{
                                    width: '100%', height: '36px', fontSize: '0.9rem',
                                    border: `2px solid ${appState.mode === 'view' ? '#e5e7eb' : '#3b82f6'}`,
                                    borderRadius: '6px',
                                    backgroundColor: appState.mode === 'view' ? '#f9fafb' : '#ffffff'
                                }}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '8px', alignItems: 'end' }}>
                            {appState.editar ? (
                                <Button 
                                    label="Actualizar" 
                                    icon="pi pi-check" 
                                    onClick={actualizarProducto}
                                    disabled={appState.mode === 'view'} 
                                    size="small"
                                    severity="warning"
                                    style={{ 
                                        height: '36px', borderRadius: '6px', fontWeight: '600'
                                    }}
                                />
                            ) : (
                                <Button 
                                    label="Agregar" 
                                    icon="pi pi-plus" 
                                    onClick={agregarProducto}
                                    disabled={appState.mode === 'view'} 
                                    size="small"
                                    severity="success"
                                    style={{ 
                                        height: '36px', borderRadius: '6px', fontWeight: '600'
                                    }}
                                />
                            )}
                            <Button 
                                label="Limpiar" 
                                icon="pi pi-times" 
                                onClick={limpiarCamposProducto}
                                disabled={appState.mode === 'view'} 
                                size="small"
                                severity="secondary"
                                outlined
                                style={{ 
                                    height: '36px', borderRadius: '6px'
                                }}
                            />
                        </div>
                    </div>

                    {/* Tabla de productos */}
                    <DataTable 
                        value={products} 
                        responsiveLayout="scroll"
                        stripedRows 
                        size="small"
                        emptyMessage="📭 No hay productos agregados a esta entrada"
                        rowHover
                        style={{ 
                            fontSize: '0.85rem', borderRadius: '8px',
                            overflow: 'hidden', border: '1px solid #e5e7eb'
                        }}
                    >
                        <Column 
                            field="id" 
                            header="ID" 
                            style={{ 
                                width: '50px', textAlign: 'center',
                                padding: '6px 8px', fontSize: '0.8rem'
                            }}
                            headerStyle={{ 
                                padding: '8px', fontSize: '0.85rem', fontWeight: '600'
                            }}
                        />
                        <Column 
                            field="producto" 
                            header="🔑 Producto" 
                            style={{ 
                                width: '100px', textAlign: 'center',
                                padding: '6px 8px', fontSize: '0.8rem'
                            }}
                            headerStyle={{ 
                                padding: '8px', fontSize: '0.85rem', fontWeight: '600'
                            }}
                        />
                        <Column 
                            field="descripcion" 
                            header="📝 Descripción" 
                            style={{ 
                                minWidth: '200px', padding: '6px 8px', fontSize: '0.8rem'
                            }}
                            headerStyle={{ 
                                padding: '8px', fontSize: '0.85rem', fontWeight: '600'
                            }}
                        />
                        <Column 
                            field="cantidad" 
                            header="📊 Cantidad" 
                            body={cantidadBodyTemplate}
                            style={{ 
                                width: '80px', textAlign: 'center', padding: '6px 8px'
                            }}
                            headerStyle={{ 
                                padding: '8px', fontSize: '0.85rem', fontWeight: '600'
                            }}
                        />
                        <Column 
                            body={actionBodyTemplate} 
                            header="⚙️ Acciones" 
                            style={{ 
                                width: '100px', textAlign: 'center', padding: '6px 8px'
                            }}
                            headerStyle={{ 
                                padding: '8px', fontSize: '0.85rem', fontWeight: '600'
                            }}
                        />
                    </DataTable>
                </div>
            </div>
        </>
    );
};

export default Entradas;